import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AddMemberModal from './AddMemberModal';
import ViewMembersModal from './ViewMembersModal';
import CreateContractTemplateModal from './CreateContractTemplateModal';
import CreateDeclarationTemplateModal from './CreateDeclarationTemplateModal';
import CreatePtoDecisionTemplateModal from './CreatePtoDecisionTemplateModal';
import { SUPPORTED_CURRENCIES, getDefaultConversionRate } from '../../utils/currency';
import { htmlToPlainText } from '../../utils/htmlToPlainText';
import { detectDefaultLengthUnit } from '../../utils/lengthUnit';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import './Salon.css';

const ManageSettings = ({ salonId, userRole, salon, onSalonUpdate }) => {
  const { t, availableLanguages } = useLanguage();
  const { refreshSettings } = useSalon();
  const [members, setMembers] = useState([]);
  const [salonName, setSalonName] = useState(salon?.name ?? '');
  const [salonDescription, setSalonDescription] = useState(salon?.description ?? '');
  const [salonSlug, setSalonSlug] = useState(salon?.slug ?? '');
  const [isUpdatingSalon, setIsUpdatingSalon] = useState(false);
  const [salonUpdateError, setSalonUpdateError] = useState('');

  useEffect(() => {
    if (salon) {
      setSalonName(salon.name ?? '');
      setSalonDescription(salon.description ?? '');
      setSalonSlug(salon.slug ?? '');
    }
  }, [salon]);

  const suggestSlug = () => {
    const s = (salonName || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    if (s) setSalonSlug(s);
  };

  const handleSaveSalonProfile = async () => {
    if (!salonId || (userRole !== 'owner' && userRole !== 'admin')) return;
    setSalonUpdateError('');
    setIsUpdatingSalon(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/salons/${salonId}`, {
        name: salonName.trim(),
        description: salonDescription.trim(),
        slug: salonSlug.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (typeof onSalonUpdate === 'function') onSalonUpdate();
    } catch (err) {
      setSalonUpdateError(err.response?.data?.error || 'Failed to update salon');
    } finally {
      setIsUpdatingSalon(false);
    }
  };

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [error, setError] = useState('');
  const [contractsPage, setContractsPage] = useState(1);
  const [contractsTotal, setContractsTotal] = useState(0);
  const [contractsPerPage] = useState(10);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isViewMembersModalOpen, setIsViewMembersModalOpen] = useState(false);
  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);

  const [declarations, setDeclarations] = useState([]);
  const [declarationsLoading, setDeclarationsLoading] = useState(false);
  const [isCreateDeclarationModalOpen, setIsCreateDeclarationModalOpen] = useState(false);
  const [editingDeclaration, setEditingDeclaration] = useState(null);
  const [isLoadingDeclaration, setIsLoadingDeclaration] = useState(false);
  const [isDeleteDeclarationModalOpen, setIsDeleteDeclarationModalOpen] = useState(false);
  const [declarationToDelete, setDeclarationToDelete] = useState(null);

  const [ptoDecisionTemplates, setPtoDecisionTemplates] = useState([]);
  const [ptoDecisionTemplatesLoading, setPtoDecisionTemplatesLoading] = useState(false);
  const [isCreatePtoDecisionModalOpen, setIsCreatePtoDecisionModalOpen] = useState(false);
  const [editingPtoDecisionTemplate, setEditingPtoDecisionTemplate] = useState(null);
  const [isLoadingPtoDecisionTemplate, setIsLoadingPtoDecisionTemplate] = useState(false);
  const [isDeletePtoDecisionModalOpen, setIsDeletePtoDecisionModalOpen] = useState(false);
  const [ptoDecisionTemplateToDelete, setPtoDecisionTemplateToDelete] = useState(null);
  
  // Referral sources state
  const [referralSources, setReferralSources] = useState([]);
  const [isAddingSource, setIsAddingSource] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceDescription, setNewSourceDescription] = useState('');
  
  // General settings state
  const [bufferDays, setBufferDays] = useState(2);
  const [isUpdatingBufferDays, setIsUpdatingBufferDays] = useState(false);

  // Extra address BCC'd whenever a PTO decision is emailed to an employee
  const [ptoDecisionBccEmail, setPtoDecisionBccEmail] = useState('');
  const [isUpdatingPtoDecisionBccEmail, setIsUpdatingPtoDecisionBccEmail] = useState(false);

  // Default deposit setting
  const [defaultDeposit, setDefaultDeposit] = useState(0);
  const [isUpdatingDefaultDeposit, setIsUpdatingDefaultDeposit] = useState(false);
  
  // Currency setting
  const [currency, setCurrency] = useState('EUR');
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false);

  // Product page (public) display language / currency / conversion rate
  const [productPageLanguage, setProductPageLanguage] = useState('en');
  const [productPageCurrency, setProductPageCurrency] = useState('EUR');
  const [productPageCurrencyExplicit, setProductPageCurrencyExplicit] = useState(false);
  const [productPageConversionRate, setProductPageConversionRate] = useState('1');
  const [isUpdatingProductPage, setIsUpdatingProductPage] = useState(false);

  // Length unit (cm / in) for declaration paper size, etc.
  const [lengthUnit, setLengthUnit] = useState(detectDefaultLengthUnit());
  const [isUpdatingLengthUnit, setIsUpdatingLengthUnit] = useState(false);

  // AI Try On (Gemini) API key
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [isUpdatingGeminiApiKey, setIsUpdatingGeminiApiKey] = useState(false);
  
  // Reservation edit lock days setting
  const [editLockDays, setEditLockDays] = useState(30);
  const [isUpdatingEditLockDays, setIsUpdatingEditLockDays] = useState(false);

  // Calendar settings state
  const [calendarViewDesktop, setCalendarViewDesktop] = useState('week');
  const [calendarViewMobile, setCalendarViewMobile] = useState('week');
  const [isUpdatingCalendarSettings, setIsUpdatingCalendarSettings] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      setError('Failed to fetch salon members');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  const fetchContracts = useCallback(async (page = 1) => {
    try {
      setContractsLoading(true);
      setError(''); // Clear any previous errors
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`/api/salons/${salonId}/contracts?page=${page}&limit=${contractsPerPage}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContracts(response.data.contracts || []);
      setContractsTotal(response.data.total || 0);
      setContractsPage(page);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setError('Failed to fetch contracts');
    } finally {
      setContractsLoading(false);
    }
  }, [salonId, contractsPerPage]);

  const fetchDeclarations = useCallback(async () => {
    try {
      setDeclarationsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/declarations?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeclarations(response.data.declarations || []);
    } catch (error) {
      console.error('Error fetching declarations:', error);
      setError('Failed to fetch declaration templates');
    } finally {
      setDeclarationsLoading(false);
    }
  }, [salonId]);

  const fetchPtoDecisionTemplates = useCallback(async () => {
    try {
      setPtoDecisionTemplatesLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/pto/decision-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPtoDecisionTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching PTO decision templates:', error);
      setError('Failed to fetch PTO decision templates');
    } finally {
      setPtoDecisionTemplatesLoading(false);
    }
  }, [salonId]);

  const fetchBufferDaysSetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/unavailable_dress_buffer_days`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value !== undefined) {
        setBufferDays(response.data.value);
      }
    } catch (error) {
      // If setting doesn't exist, use default value (2)
      console.log('Buffer days setting not found, using default value');
    }
  }, [salonId]);

  const fetchPtoDecisionBccEmailSetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/pto_decision_bcc_email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value !== undefined && response.data.value !== null) {
        setPtoDecisionBccEmail(response.data.value);
      }
    } catch (error) {
      // If setting doesn't exist, use default value (empty - no extra address)
      console.log('PTO decision BCC email setting not found, using default value');
    }
  }, [salonId]);

  const fetchDefaultDepositSetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/default_deposit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value !== undefined) {
        setDefaultDeposit(response.data.value);
      }
    } catch (error) {
      // If setting doesn't exist, use default value (0)
      console.log('Default deposit setting not found, using default value');
    }
  }, [salonId]);

  const fetchCurrencySetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value !== undefined) {
        setCurrency(response.data.value);
      }
    } catch (error) {
      // If setting doesn't exist, use default value (EUR)
      console.log('Currency setting not found, using default value');
    }
  }, [salonId]);

  const fetchProductPageSettings = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const response = await axios.get(`/api/salons/${salonId}/settings/product_page_language`, { headers });
      if (response.data.value !== undefined) {
        setProductPageLanguage(response.data.value);
      }
    } catch (error) {
      console.log('Product page language setting not found, using default value');
    }

    try {
      const response = await axios.get(`/api/salons/${salonId}/settings/product_page_currency`, { headers });
      if (response.data.value !== undefined) {
        setProductPageCurrency(response.data.value);
        setProductPageCurrencyExplicit(true);
      }
    } catch (error) {
      // Not saved yet - stays synced to the salon's base currency (see effect below).
      console.log('Product page currency setting not found, using default value');
    }

    try {
      const response = await axios.get(`/api/salons/${salonId}/settings/product_page_conversion_rate`, { headers });
      if (response.data.value !== undefined) {
        setProductPageConversionRate(String(response.data.value));
      }
    } catch (error) {
      console.log('Product page conversion rate setting not found, using default value');
    }
  }, [salonId]);

  const fetchLengthUnitSetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/length_unit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value === 'cm' || response.data.value === 'in') {
        setLengthUnit(response.data.value);
      }
    } catch (error) {
      setLengthUnit(detectDefaultLengthUnit());
    }
  }, [salonId]);

  const fetchGeminiApiKeySetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/gemini_api_key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value !== undefined && response.data.value !== null) {
        setGeminiApiKey(String(response.data.value));
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.log('Gemini API key setting not found or error');
      }
    }
  }, [salonId]);

  const updateBufferDays = async (newValue) => {
    try {
      setIsUpdatingBufferDays(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'unavailable_dress_buffer_days',
        value: newValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBufferDays(newValue);
    } catch (error) {
      console.error('Error updating buffer days:', error);
      setError('Failed to update buffer days setting');
    } finally {
      setIsUpdatingBufferDays(false);
    }
  };

  const updatePtoDecisionBccEmail = async (newValue) => {
    const trimmed = newValue.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(t('settings.invalidEmail', 'Please enter a valid email address'));
      return;
    }
    try {
      setIsUpdatingPtoDecisionBccEmail(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'pto_decision_bcc_email',
        value: trimmed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPtoDecisionBccEmail(trimmed);
    } catch (error) {
      console.error('Error updating PTO decision BCC email:', error);
      setError('Failed to update PTO decision BCC email setting');
    } finally {
      setIsUpdatingPtoDecisionBccEmail(false);
    }
  };

  const updateDefaultDeposit = async (newValue) => {
    try {
      setIsUpdatingDefaultDeposit(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'default_deposit',
        value: parseFloat(newValue) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultDeposit(parseFloat(newValue) || 0);
    } catch (error) {
      console.error('Error updating default deposit:', error);
      setError('Failed to update default deposit setting');
    } finally {
      setIsUpdatingDefaultDeposit(false);
    }
  };

  const updateCurrency = async (newValue) => {
    try {
      setIsUpdatingCurrency(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'currency',
        value: newValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrency(newValue);
      // Refresh salon settings in context so other components get the update
      refreshSettings();
    } catch (error) {
      console.error('Error updating currency:', error);
      setError('Failed to update currency setting');
    } finally {
      setIsUpdatingCurrency(false);
    }
  };

  const handleProductPageCurrencyChange = (code) => {
    setProductPageCurrency(code);
    setProductPageCurrencyExplicit(true);
    setProductPageConversionRate(getDefaultConversionRate(currency, code));
  };

  const updateProductPageSettings = async () => {
    const rate = parseFloat(productPageConversionRate);
    if (productPageCurrency !== currency && (Number.isNaN(rate) || rate <= 0)) {
      setError(t('settings.productPageInvalidRate', 'Conversion rate must be greater than 0.'));
      return;
    }

    try {
      setIsUpdatingProductPage(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`/api/salons/${salonId}/settings`, { key: 'product_page_language', value: productPageLanguage }, { headers });
      await axios.post(`/api/salons/${salonId}/settings`, { key: 'product_page_currency', value: productPageCurrency }, { headers });
      await axios.post(`/api/salons/${salonId}/settings`, { key: 'product_page_conversion_rate', value: productPageCurrency === currency ? 1 : rate }, { headers });
      setProductPageCurrencyExplicit(true);
      setError('');
    } catch (error) {
      console.error('Error updating product page settings:', error);
      setError('Failed to update product page settings');
    } finally {
      setIsUpdatingProductPage(false);
    }
  };

  const updateLengthUnit = async (newValue) => {
    try {
      setIsUpdatingLengthUnit(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'length_unit',
        value: newValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLengthUnit(newValue);
      refreshSettings();
    } catch (error) {
      console.error('Error updating length unit:', error);
      setError('Failed to update length unit setting');
    } finally {
      setIsUpdatingLengthUnit(false);
    }
  };

  const updateGeminiApiKey = async (newValue) => {
    try {
      setIsUpdatingGeminiApiKey(true);
      setError('');
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'gemini_api_key',
        value: newValue || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeminiApiKey(newValue || '');
    } catch (error) {
      console.error('Error updating Gemini API key:', error);
      setError('Failed to update AI Try On API key');
    } finally {
      setIsUpdatingGeminiApiKey(false);
    }
  };

  const fetchEditLockDaysSetting = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/settings/reservation_edit_lock_days`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.value !== undefined) {
        setEditLockDays(response.data.value);
      }
    } catch (error) {
      console.log('Edit lock days setting not found, using default value');
    }
  }, [salonId]);

  const updateEditLockDays = async (newValue) => {
    try {
      setIsUpdatingEditLockDays(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'reservation_edit_lock_days',
        value: parseInt(newValue) || 30
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditLockDays(parseInt(newValue) || 30);
      refreshSettings();
    } catch (error) {
      console.error('Error updating edit lock days:', error);
      setError('Failed to update reservation edit lock days setting');
    } finally {
      setIsUpdatingEditLockDays(false);
    }
  };

  const fetchCalendarSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch desktop calendar view setting
      try {
        const desktopResponse = await axios.get(`/api/salons/${salonId}/settings/calendar_view_desktop`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (desktopResponse.data.value !== undefined) {
          setCalendarViewDesktop(desktopResponse.data.value);
        }
      } catch (error) {
        console.log('Desktop calendar view setting not found, using default');
      }
      
      // Fetch mobile calendar view setting
      try {
        const mobileResponse = await axios.get(`/api/salons/${salonId}/settings/calendar_view_mobile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (mobileResponse.data.value !== undefined) {
          setCalendarViewMobile(mobileResponse.data.value);
        }
      } catch (error) {
        console.log('Mobile calendar view setting not found, using default');
      }
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
    }
  }, [salonId]);

  const updateCalendarSettings = async () => {
    try {
      setIsUpdatingCalendarSettings(true);
      const token = localStorage.getItem('token');
      
      // Update desktop calendar view
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'calendar_view_desktop',
        value: calendarViewDesktop
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update mobile calendar view
      await axios.post(`/api/salons/${salonId}/settings`, {
        key: 'calendar_view_mobile',
        value: calendarViewMobile
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
    } catch (error) {
      console.error('Error updating calendar settings:', error);
      setError('Failed to update calendar settings');
    } finally {
      setIsUpdatingCalendarSettings(false);
    }
  };

  const fetchReferralSources = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/referral-sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralSources(response.data.sources || []);
    } catch (error) {
      console.error('Error fetching referral sources:', error);
    }
  }, [salonId]);

  useEffect(() => {
    fetchMembers();
    fetchContracts();
    fetchDeclarations();
    fetchPtoDecisionTemplates();
    fetchBufferDaysSetting();
    fetchPtoDecisionBccEmailSetting();
    fetchDefaultDepositSetting();
    fetchEditLockDaysSetting();
    fetchCurrencySetting();
    fetchProductPageSettings();
    fetchLengthUnitSetting();
    fetchGeminiApiKeySetting();
    fetchCalendarSettings();
    fetchReferralSources();
  }, [fetchMembers, fetchContracts, fetchDeclarations, fetchPtoDecisionTemplates, fetchBufferDaysSetting, fetchPtoDecisionBccEmailSetting, fetchDefaultDepositSetting, fetchEditLockDaysSetting, fetchCurrencySetting, fetchProductPageSettings, fetchLengthUnitSetting, fetchGeminiApiKeySetting, fetchCalendarSettings, fetchReferralSources]);

  // Until the salon explicitly picks a product-page display currency, keep it in sync
  // with the salon's base currency so an untouched product page shows no conversion.
  useEffect(() => {
    if (!productPageCurrencyExplicit) {
      setProductPageCurrency(currency);
    }
  }, [currency, productPageCurrencyExplicit]);

  const handleMemberAdded = () => {
    fetchMembers();
    setIsAddMemberModalOpen(false);
  };

  const handleViewMembersClick = () => {
    setIsViewMembersModalOpen(true);
  };

  const handleAddMemberClick = () => {
    setIsAddMemberModalOpen(true);
  };

  const handleCreateTemplateClick = () => {
    setEditingContract(null);
    setIsCreateTemplateModalOpen(true);
  };

  const handleEditContractClick = async (contract) => {
    try {
      setIsLoadingContract(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/contracts/${contract.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditingContract(response.data.contract);
      setIsCreateTemplateModalOpen(true);
    } catch (error) {
      console.error('Error fetching contract details:', error);
      setError('Failed to load contract details');
    } finally {
      setIsLoadingContract(false);
    }
  };

  const handleTemplateCreated = () => {
    fetchContracts();
    setIsCreateTemplateModalOpen(false);
    setEditingContract(null);
  };

  const handleDeleteContractClick = (contract) => {
    setContractToDelete(contract);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteContract = async () => {
    if (!contractToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/contracts/${contractToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh contracts list
      fetchContracts();
      setIsDeleteModalOpen(false);
      setContractToDelete(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
      setError('Failed to delete contract');
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setContractToDelete(null);
  };

  const handleContractsPageChange = (newPage) => {
    fetchContracts(newPage);
  };

  const handleCreateDeclarationClick = () => {
    setEditingDeclaration(null);
    setIsCreateDeclarationModalOpen(true);
  };

  const handleEditDeclarationClick = async (declaration) => {
    try {
      setIsLoadingDeclaration(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/declarations/${declaration.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingDeclaration(response.data.declaration);
      setIsCreateDeclarationModalOpen(true);
    } catch (error) {
      console.error('Error fetching declaration details:', error);
      setError(t('settings.failedToLoadDeclaration', 'Failed to load declaration details'));
    } finally {
      setIsLoadingDeclaration(false);
    }
  };

  const handleDeclarationCreated = () => {
    fetchDeclarations();
    setIsCreateDeclarationModalOpen(false);
    setEditingDeclaration(null);
  };

  const handleDeleteDeclarationClick = (declaration) => {
    setDeclarationToDelete(declaration);
    setIsDeleteDeclarationModalOpen(true);
  };

  const handleDeleteDeclaration = async () => {
    if (!declarationToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/declarations/${declarationToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDeclarations();
      setIsDeleteDeclarationModalOpen(false);
      setDeclarationToDelete(null);
    } catch (error) {
      console.error('Error deleting declaration:', error);
      setError('Failed to delete declaration template');
    }
  };

  const handleCancelDeleteDeclaration = () => {
    setIsDeleteDeclarationModalOpen(false);
    setDeclarationToDelete(null);
  };

  const handleCreatePtoDecisionClick = () => {
    setEditingPtoDecisionTemplate(null);
    setIsCreatePtoDecisionModalOpen(true);
  };

  const handleEditPtoDecisionClick = async (template) => {
    try {
      setIsLoadingPtoDecisionTemplate(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/pto/decision-templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPtoDecisionTemplate(response.data.template);
      setIsCreatePtoDecisionModalOpen(true);
    } catch (error) {
      console.error('Error fetching PTO decision template details:', error);
      setError('Failed to load PTO decision template details');
    } finally {
      setIsLoadingPtoDecisionTemplate(false);
    }
  };

  const handlePtoDecisionCreated = () => {
    fetchPtoDecisionTemplates();
    setIsCreatePtoDecisionModalOpen(false);
    setEditingPtoDecisionTemplate(null);
  };

  const handleDeletePtoDecisionClick = (template) => {
    setPtoDecisionTemplateToDelete(template);
    setIsDeletePtoDecisionModalOpen(true);
  };

  const handleDeletePtoDecision = async () => {
    if (!ptoDecisionTemplateToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/pto/decision-templates/${ptoDecisionTemplateToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPtoDecisionTemplates();
      setIsDeletePtoDecisionModalOpen(false);
      setPtoDecisionTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting PTO decision template:', error);
      setError('Failed to delete PTO decision template');
    }
  };

  const handleCancelDeletePtoDecision = () => {
    setIsDeletePtoDecisionModalOpen(false);
    setPtoDecisionTemplateToDelete(null);
  };

  const handleAddReferralSource = async () => {
    if (!newSourceName.trim()) {
      setError('Please enter a source name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/referral-sources`, {
        source_name: newSourceName,
        description: newSourceDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewSourceName('');
      setNewSourceDescription('');
      setIsAddingSource(false);
      fetchReferralSources();
    } catch (error) {
      console.error('Error adding referral source:', error);
      setError('Failed to add referral source');
    }
  };

  const handleUpdateReferralSource = async (sourceId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/salons/${salonId}/referral-sources/${sourceId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchReferralSources();
      setEditingSource(null);
    } catch (error) {
      console.error('Error updating referral source:', error);
      setError('Failed to update referral source');
    }
  };

  const handleDeleteReferralSource = async (sourceId) => {
    if (!window.confirm(t('messages.confirm.delete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/referral-sources/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchReferralSources();
    } catch (error) {
      console.error('Error deleting referral source:', error);
      setError('Failed to delete referral source');
    }
  };

  if (loading) {
    return (
      <div className="manage-workspace">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('settings.loadingSettings')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="table-header">
        <h2>{t('settings.title')}</h2>
        <div className="header-actions">
          <button 
            className="salon-button secondary"
            onClick={handleViewMembersClick}
          >
            {t('settings.viewMembers')}
          </button>
          {(userRole === 'owner' || userRole === 'admin') && (
            <button 
              className="salon-button primary"
              onClick={handleAddMemberClick}
            >
              {t('settings.addMember')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="settings-content">
        <div className="settings-section">
          <h3>{t('settings.salonMembers')}</h3>
          <p>{t('settings.manageMembersDesc')}</p>
          <div className="members-summary">
            <div className="summary-card">
              <div className="summary-number">{Array.isArray(members) ? members.length : 0}</div>
              <div className="summary-label">{t('settings.totalMembers')}</div>
            </div>
            <div className="summary-card">
              <div className="summary-number">
                {Array.isArray(members) ? members.filter(m => m.role === 'owner').length : 0}
              </div>
              <div className="summary-label">{t('settings.owners')}</div>
            </div>
            <div className="summary-card">
              <div className="summary-number">
                {Array.isArray(members) ? members.filter(m => m.role === 'admin').length : 0}
              </div>
              <div className="summary-label">{t('settings.admins')}</div>
            </div>
            <div className="summary-card">
              <div className="summary-number">
                {Array.isArray(members) ? members.filter(m => m.role === 'member').length : 0}
              </div>
              <div className="summary-label">{t('settings.members')}</div>
            </div>
          </div>
        </div>

        {(userRole === 'owner' || userRole === 'admin') && (
          <div className="settings-section">
            <h3>{t('settings.salonProfileAndCatalogue')}</h3>
            <p>{t('settings.salonProfileAndCatalogueDesc')}</p>
            {salonUpdateError && (
              <div className="error-message">{salonUpdateError}</div>
            )}
            <div className="setting-item setting-item-wide">
              <div className="setting-info">
                <label htmlFor="salon-name" className="setting-label">{t('settings.salonName')}</label>
                <p className="setting-description">{t('settings.salonNameDesc', 'The name shown to staff and on your public catalogue and product pages.')}</p>
              </div>
              <div className="setting-control">
                <input
                  id="salon-name"
                  type="text"
                  value={salonName}
                  onChange={(e) => setSalonName(e.target.value)}
                  disabled={isUpdatingSalon}
                  className="setting-input setting-input-wide"
                  placeholder={t('settings.salonNamePlaceholder')}
                />
              </div>
            </div>
            <div className="setting-item setting-item-wide">
              <div className="setting-info">
                <label htmlFor="salon-description" className="setting-label">{t('settings.salonDescription')}</label>
                <p className="setting-description">{t('settings.salonDescriptionDesc', 'A short description shown at the top of your public catalogue page.')}</p>
              </div>
              <div className="setting-control">
                <textarea
                  id="salon-description"
                  value={salonDescription}
                  onChange={(e) => setSalonDescription(e.target.value)}
                  disabled={isUpdatingSalon}
                  className="setting-input setting-input-wide"
                  rows={3}
                  placeholder={t('settings.salonDescriptionPlaceholder')}
                />
              </div>
            </div>
            <div className="setting-item setting-item-wide">
              <div className="setting-info">
                <label htmlFor="salon-slug" className="setting-label">{t('settings.publicCatalogueSlug')}</label>
                <p className="setting-description">{t('settings.publicCatalogueSlugDesc')}</p>
              </div>
              <div className="setting-control setting-control-stack">
                <input
                  id="salon-slug"
                  type="text"
                  value={salonSlug}
                  onChange={(e) => setSalonSlug(e.target.value)}
                  disabled={isUpdatingSalon}
                  className="setting-input setting-input-wide"
                  placeholder="e.g. i-do"
                />
                <button type="button" className="salon-button secondary" onClick={suggestSlug} disabled={isUpdatingSalon}>
                  {t('settings.suggestFromName')}
                </button>
              </div>
            </div>
            {salonSlug.trim() && (
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">{t('settings.publicCatalogueLink')}</span>
                </div>
                <div className="setting-control">
                  <a href={`${window.location.origin}/shops/${salonSlug.trim()}`} target="_blank" rel="noopener noreferrer" className="public-catalogue-link">
                    {window.location.origin}/shops/{salonSlug.trim()}
                  </a>
                </div>
              </div>
            )}
            <div className="settings-section-footer">
              <button
                className="salon-button primary"
                onClick={handleSaveSalonProfile}
                disabled={isUpdatingSalon}
              >
                {isUpdatingSalon ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>
        )}

        <div className="settings-section">
          <h3>{t('settings.generalSettings')}</h3>
          <p>{t('settings.generalSettingsDesc')}</p>
          
          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="buffer-days" className="setting-label">
                {t('settings.unavailableDressBuffer')}
              </label>
              <p className="setting-description">
                {t('settings.bufferDaysDesc')}
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <input
                id="buffer-days"
                type="number"
                min="0"
                max="30"
                value={bufferDays}
                onChange={(e) => setBufferDays(parseInt(e.target.value) || 0)}
                disabled={isUpdatingBufferDays}
                className="setting-input setting-input-narrow"
              />
              <button
                className="salon-button primary"
                onClick={() => updateBufferDays(bufferDays)}
                disabled={isUpdatingBufferDays}
              >
                {isUpdatingBufferDays ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="pto-decision-bcc-email" className="setting-label">
                {t('settings.ptoDecisionBccEmail')}
              </label>
              <p className="setting-description">
                {t('settings.ptoDecisionBccEmailDesc')}
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <input
                id="pto-decision-bcc-email"
                type="email"
                value={ptoDecisionBccEmail}
                onChange={(e) => setPtoDecisionBccEmail(e.target.value)}
                disabled={isUpdatingPtoDecisionBccEmail}
                className="setting-input setting-input-wide"
                placeholder={t('settings.ptoDecisionBccEmailPlaceholder')}
              />
              <button
                className="salon-button primary"
                onClick={() => updatePtoDecisionBccEmail(ptoDecisionBccEmail)}
                disabled={isUpdatingPtoDecisionBccEmail}
              >
                {isUpdatingPtoDecisionBccEmail ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="default-deposit" className="setting-label">
                {t('settings.defaultDeposit')}
              </label>
              <p className="setting-description">
                {t('settings.defaultDepositDesc')}
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <input
                id="default-deposit"
                type="number"
                min="0"
                step="0.01"
                value={defaultDeposit}
                onChange={(e) => setDefaultDeposit(parseFloat(e.target.value) || 0)}
                disabled={isUpdatingDefaultDeposit}
                className="setting-input setting-input-narrow"
              />
              <button
                className="salon-button primary"
                onClick={() => updateDefaultDeposit(defaultDeposit)}
                disabled={isUpdatingDefaultDeposit}
              >
                {isUpdatingDefaultDeposit ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="edit-lock-days" className="setting-label">
                {t('settings.reservationEditLockDays')}
              </label>
              <p className="setting-description">
                {t('settings.reservationEditLockDaysDesc')}
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <input
                id="edit-lock-days"
                type="number"
                min="0"
                max="365"
                value={editLockDays}
                onChange={(e) => setEditLockDays(parseInt(e.target.value) || 0)}
                disabled={isUpdatingEditLockDays}
                className="setting-input setting-input-narrow"
              />
              <button
                className="salon-button primary"
                onClick={() => updateEditLockDays(editLockDays)}
                disabled={isUpdatingEditLockDays}
              >
                {isUpdatingEditLockDays ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="currency" className="setting-label">
                {t('settings.currency')}
              </label>
              <p className="setting-description">
                {t('settings.currencyDesc')}
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={isUpdatingCurrency}
                className="setting-select setting-input-narrow"
              >
                {SUPPORTED_CURRENCIES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
              <button
                className="salon-button primary"
                onClick={() => updateCurrency(currency)}
                disabled={isUpdatingCurrency}
              >
                {isUpdatingCurrency ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="length_unit" className="setting-label">
                {t('settings.lengthUnit')}
              </label>
              <p className="setting-description">
                {t('settings.lengthUnitDesc')}
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <select
                id="length_unit"
                value={lengthUnit}
                onChange={(e) => setLengthUnit(e.target.value)}
                disabled={isUpdatingLengthUnit}
                className="setting-select setting-input-narrow"
              >
                <option value="cm">{t('settings.centimeters')}</option>
                <option value="in">{t('settings.inches')}</option>
              </select>
              <button
                className="salon-button primary"
                onClick={() => updateLengthUnit(lengthUnit)}
                disabled={isUpdatingLengthUnit}
              >
                {isUpdatingLengthUnit ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="gemini_api_key" className="setting-label">
                AI Try On (Gemini) API key
              </label>
              <p className="setting-description">
                Optional. Set a Google Gemini API key to enable the AI Try On feature in your public dress catalogue. Each salon uses its own key.
              </p>
            </div>
            <div className="setting-control setting-control-actions">
              <input
                type="password"
                id="gemini_api_key"
                value={geminiApiKey}
                onChange={(e) => setGeminiApiKey(e.target.value)}
                disabled={isUpdatingGeminiApiKey}
                className="setting-input setting-input-wide"
                placeholder="Enter API key"
                autoComplete="off"
              />
              <button
                className="salon-button primary"
                onClick={() => updateGeminiApiKey(geminiApiKey)}
                disabled={isUpdatingGeminiApiKey}
              >
                {isUpdatingGeminiApiKey ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label className="setting-label">
                {t('settings.calendarDefaultViews')}
              </label>
              <p className="setting-description">
                {t('settings.calendarViewsDesc')}
              </p>
            </div>
            <div className="setting-control calendar-settings">
              <div className="calendar-view-setting">
                <label htmlFor="desktop-calendar-view" className="calendar-label">{t('settings.desktop')}:</label>
                <select
                  id="desktop-calendar-view"
                  value={calendarViewDesktop}
                  onChange={(e) => setCalendarViewDesktop(e.target.value)}
                  disabled={isUpdatingCalendarSettings}
                  className="setting-select"
                >
                  <option value="day">{t('settings.day')}</option>
                  <option value="week">{t('settings.week')}</option>
                  <option value="month">{t('settings.month')}</option>
                </select>
              </div>
              <div className="calendar-view-setting">
                <label htmlFor="mobile-calendar-view" className="calendar-label">{t('settings.mobile')}:</label>
                <select
                  id="mobile-calendar-view"
                  value={calendarViewMobile}
                  onChange={(e) => setCalendarViewMobile(e.target.value)}
                  disabled={isUpdatingCalendarSettings}
                  className="setting-select"
                >
                  <option value="day">{t('settings.day')}</option>
                  <option value="week">{t('settings.week')}</option>
                  <option value="month">{t('settings.month')}</option>
                </select>
              </div>
              <button
                className="salon-button primary"
                onClick={updateCalendarSettings}
                disabled={isUpdatingCalendarSettings}
              >
                {isUpdatingCalendarSettings ? t('settings.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('settings.productPage', 'Product page')}</h3>
          <p>{t('settings.productPageDesc', 'Choose the language, currency, and conversion rate used on your public dress product pages.')}</p>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="product-page-language" className="setting-label">
                {t('settings.productPageLanguage', 'Display language')}
              </label>
              <p className="setting-description">
                {t('settings.productPageLanguageDesc', 'Language used for text on public dress product pages.')}
              </p>
            </div>
            <div className="setting-control">
              <select
                id="product-page-language"
                value={productPageLanguage}
                onChange={(e) => setProductPageLanguage(e.target.value)}
                disabled={isUpdatingProductPage}
                className="setting-select setting-input-narrow"
              >
                {availableLanguages.map(({ code, nativeName }) => (
                  <option key={code} value={code}>{nativeName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="product-page-currency" className="setting-label">
                {t('settings.productPageCurrency', 'Display currency')}
              </label>
              <p className="setting-description">
                {t('settings.productPageCurrencyDesc', 'Currency used to show prices on public dress product pages.')}
              </p>
            </div>
            <div className="setting-control">
              <select
                id="product-page-currency"
                value={productPageCurrency}
                onChange={(e) => handleProductPageCurrencyChange(e.target.value)}
                disabled={isUpdatingProductPage}
                className="setting-select setting-input-narrow"
              >
                {SUPPORTED_CURRENCIES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="setting-item setting-item-wide">
            <div className="setting-info">
              <label htmlFor="product-page-conversion-rate" className="setting-label">
                {t('settings.productPageConversionRate', 'Conversion rate')}
              </label>
              <p className="setting-description">
                {productPageCurrency === currency
                  ? t('settings.productPageSameCurrencyHint', 'Conversion rate is not needed when the product page uses the same currency as the salon.')
                  : t(
                    'settings.productPageConversionRateHint',
                    'Multiply prices in {dbCurrency} by this rate to get {targetCurrency}.',
                    { dbCurrency: currency, targetCurrency: productPageCurrency }
                  )}
              </p>
            </div>
            <div className="setting-control">
              <input
                id="product-page-conversion-rate"
                type="number"
                min="0"
                step="any"
                value={productPageConversionRate}
                onChange={(e) => setProductPageConversionRate(e.target.value)}
                disabled={isUpdatingProductPage || productPageCurrency === currency}
                className="setting-input setting-input-narrow"
              />
            </div>
          </div>

          <div className="settings-section-footer">
            <button
              className="salon-button primary"
              onClick={updateProductPageSettings}
              disabled={isUpdatingProductPage}
            >
              {isUpdatingProductPage ? t('settings.saving') : t('common.save')}
            </button>
          </div>
        </div>

        <div className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3>{t('settings.referralSources')}</h3>
              <p>{t('settings.referralSourcesDesc')}</p>
            </div>
            {(userRole === 'owner' || userRole === 'admin') && !isAddingSource && (
              <button 
                className="salon-button primary"
                onClick={() => setIsAddingSource(true)}
              >
                {t('settings.addReferralSource')}
              </button>
            )}
          </div>

          {isAddingSource && (
            <div className="add-source-form">
              <div className="form-group">
                <label htmlFor="source-name">{t('settings.sourceName')} *</label>
                <input
                  id="source-name"
                  type="text"
                  value={newSourceName}
                  onChange={(e) => setNewSourceName(e.target.value)}
                  placeholder={t('settings.sourceNamePlaceholder')}
                  className="setting-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="source-description">{t('settings.sourceDescription')}</label>
                <textarea
                  id="source-description"
                  value={newSourceDescription}
                  onChange={(e) => setNewSourceDescription(e.target.value)}
                  placeholder={t('settings.sourceDescriptionPlaceholder')}
                  className="setting-input"
                  rows="2"
                />
              </div>
              <div className="form-actions">
                <button
                  className="salon-button secondary"
                  onClick={() => {
                    setIsAddingSource(false);
                    setNewSourceName('');
                    setNewSourceDescription('');
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="salon-button primary"
                  onClick={handleAddReferralSource}
                >
                  {t('common.add')}
                </button>
              </div>
            </div>
          )}

          <div className="referral-sources-list">
            {referralSources.map((source) => (
              <div key={source.id} className="source-item">
                {editingSource?.id === source.id ? (
                  <div className="edit-source-form">
                    <div className="form-group">
                      <input
                        type="text"
                        value={editingSource.source_name}
                        onChange={(e) => setEditingSource({...editingSource, source_name: e.target.value})}
                        className="setting-input"
                      />
                    </div>
                    <div className="form-group">
                      <textarea
                        value={editingSource.description || ''}
                        onChange={(e) => setEditingSource({...editingSource, description: e.target.value})}
                        className="setting-input"
                        rows="2"
                      />
                    </div>
                    <div className="form-actions">
                      <button
                        className="salon-button secondary"
                        onClick={() => setEditingSource(null)}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="salon-button primary"
                        onClick={() => handleUpdateReferralSource(source.id, {
                          source_name: editingSource.source_name,
                          description: editingSource.description
                        })}
                      >
                        {t('common.save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="source-info">
                      <div className="source-name">{source.source_name}</div>
                      {source.description && (
                        <div className="source-description">{source.description}</div>
                      )}
                    </div>
                    {(userRole === 'owner' || userRole === 'admin') && (
                      <div className="source-actions">
                        <button
                          className="action-button edit"
                          onClick={() => setEditingSource(source)}
                          title={t('common.edit')}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-button delete"
                          onClick={() => handleDeleteReferralSource(source.id)}
                          title={t('common.delete')}
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3>{t('settings.contractTemplates')}</h3>
              <p>{t('settings.contractTemplatesDesc')}</p>
            </div>
            {(userRole === 'owner' || userRole === 'admin') && (
              <button 
                className="salon-button primary"
                onClick={handleCreateTemplateClick}
              >
                {t('settings.newContractTemplate')}
              </button>
            )}
          </div>
          
          {contractsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('settings.loadingContracts')}</p>
            </div>
          ) : (
            <div className="contracts-summary">
              <div className="summary-card">
                <div className="summary-number">{contracts.length}</div>
                <div className="summary-label">{t('settings.totalTemplates')}</div>
              </div>
              <div className="summary-card">
                <div className="summary-number">
                  {contracts.filter(c => c.contract_type === 'rental').length}
                </div>
                <div className="summary-label">{t('settings.rentalContracts')}</div>
              </div>
              <div className="summary-card">
                <div className="summary-number">
                  {contracts.filter(c => c.contract_type === 'sale').length}
                </div>
                <div className="summary-label">{t('settings.saleContracts')}</div>
              </div>
            </div>
          )}

          {contracts.length > 0 && (
            <div className="recent-contracts">
              <h4>{t('settings.contractTemplates')}</h4>
              <div className="contracts-list">
                {contracts.map((contract) => (
                  <div 
                    key={contract.id} 
                    className={`contract-item clickable ${isLoadingContract ? 'loading' : ''}`}
                    onClick={() => !isLoadingContract && handleEditContractClick(contract)}
                  >
                    <div className="contract-info">
                      <div className="contract-title">{contract.title}</div>
                      <div className="contract-type">{contract.contract_type}</div>
                      <div className="contract-preview">{htmlToPlainText(contract.preview)}</div>
                    </div>
                    <div className="contract-actions">
                      <div className="contract-status">
                        {isLoadingContract ? (
                          <div className="loading-spinner-small"></div>
                        ) : (
                          <span className={`status-badge ${contract.is_active ? 'active' : 'inactive'}`}>
                            {contract.is_active ? t('settings.active') : t('settings.inactive')}
                          </span>
                        )}
                      </div>
                      {!isLoadingContract && (userRole === 'owner' || userRole === 'admin') && (
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteContractClick(contract);
                          }}
                          title={t('settings.deleteContract')}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {contractsTotal > contractsPerPage && (
                <div className="pagination-controls">
                  <button
                    className="pagination-button"
                    onClick={() => handleContractsPageChange(contractsPage - 1)}
                    disabled={contractsPage === 1}
                  >
                    {t('common.previous')}
                  </button>
                  
                  <span className="pagination-info">
                    {t('reservations.page')} {contractsPage} {t('reservations.of')} {Math.ceil(contractsTotal / contractsPerPage)}
                  </span>
                  
                  <button
                    className="pagination-button"
                    onClick={() => handleContractsPageChange(contractsPage + 1)}
                    disabled={contractsPage >= Math.ceil(contractsTotal / contractsPerPage)}
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        <div className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3>{t('settings.declarationTemplates')}</h3>
              <p>{t('settings.declarationTemplatesDesc')}</p>
            </div>
            {(userRole === 'owner' || userRole === 'admin') && (
              <button
                className="salon-button primary"
                onClick={handleCreateDeclarationClick}
              >
                {t('settings.newDeclarationTemplate')}
              </button>
            )}
          </div>

          {declarationsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('settings.loadingDeclarations')}</p>
            </div>
          ) : (
            <div className="contracts-summary">
              <div className="summary-card">
                <div className="summary-number">{declarations.length}</div>
                <div className="summary-label">{t('settings.totalTemplates')}</div>
              </div>
              <div className="summary-card">
                <div className="summary-number">
                  {declarations.filter((d) => d.is_active).length}
                </div>
                <div className="summary-label">{t('settings.activeDeclarations')}</div>
              </div>
            </div>
          )}

          {declarations.length > 0 && (
            <div className="recent-contracts">
              <h4>{t('settings.declarationTemplates')}</h4>
              <div className="contracts-list">
                {declarations.map((declaration) => (
                  <div
                    key={declaration.id}
                    className={`contract-item clickable ${isLoadingDeclaration ? 'loading' : ''}`}
                    onClick={() => !isLoadingDeclaration && handleEditDeclarationClick(declaration)}
                  >
                    <div className="contract-info">
                      <div className="contract-title">{declaration.title}</div>
                      <div className="contract-preview">{htmlToPlainText(declaration.preview)}</div>
                    </div>
                    <div className="contract-actions">
                      <div className="contract-status">
                        {isLoadingDeclaration ? (
                          <div className="loading-spinner-small"></div>
                        ) : (
                          <span className={`status-badge ${declaration.is_active ? 'active' : 'inactive'}`}>
                            {declaration.is_active ? t('settings.active') : t('settings.inactive')}
                          </span>
                        )}
                      </div>
                      {!isLoadingDeclaration && (userRole === 'owner' || userRole === 'admin') && (
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDeclarationClick(declaration);
                          }}
                          title={t('settings.deleteDeclaration')}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3>{t('timeOff.pto.decisionTemplates')}</h3>
              <p>{t('timeOff.pto.decisionTemplatesDesc')}</p>
            </div>
            {(userRole === 'owner' || userRole === 'admin') && (
              <button
                className="salon-button primary"
                onClick={handleCreatePtoDecisionClick}
              >
                {t('timeOff.pto.newDecisionTemplate')}
              </button>
            )}
          </div>

          {ptoDecisionTemplatesLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('common.loading')}...</p>
            </div>
          ) : (
            <div className="contracts-summary">
              <div className="summary-card">
                <div className="summary-number">{ptoDecisionTemplates.length}</div>
                <div className="summary-label">{t('settings.totalTemplates')}</div>
              </div>
              <div className="summary-card">
                <div className="summary-number">
                  {ptoDecisionTemplates.filter((tpl) => tpl.is_active).length}
                </div>
                <div className="summary-label">{t('settings.activeDeclarations')}</div>
              </div>
            </div>
          )}

          {ptoDecisionTemplates.length > 0 && (
            <div className="recent-contracts">
              <h4>{t('timeOff.pto.decisionTemplates')}</h4>
              <div className="contracts-list">
                {ptoDecisionTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`contract-item clickable ${isLoadingPtoDecisionTemplate ? 'loading' : ''}`}
                    onClick={() => !isLoadingPtoDecisionTemplate && handleEditPtoDecisionClick(template)}
                  >
                    <div className="contract-info">
                      <div className="contract-title">{template.title}</div>
                      <div className="contract-preview">{htmlToPlainText(template.preview)}</div>
                    </div>
                    <div className="contract-actions">
                      <div className="contract-status">
                        {isLoadingPtoDecisionTemplate ? (
                          <div className="loading-spinner-small"></div>
                        ) : (
                          <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                            {template.is_active ? t('settings.active') : t('settings.inactive')}
                          </span>
                        )}
                      </div>
                      {!isLoadingPtoDecisionTemplate && (userRole === 'owner' || userRole === 'admin') && (
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePtoDecisionClick(template);
                          }}
                          title={t('common.delete')}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        salonId={salonId}
        onMemberAdded={handleMemberAdded}
      />

      {/* View Members Modal */}
      <ViewMembersModal
        isOpen={isViewMembersModalOpen}
        onClose={() => setIsViewMembersModalOpen(false)}
        salonId={salonId}
        members={members}
        onMemberUpdated={fetchMembers}
        userRole={userRole}
      />

      {/* Create Contract Template Modal */}
      <CreateContractTemplateModal
        isOpen={isCreateTemplateModalOpen}
        onClose={() => {
          setIsCreateTemplateModalOpen(false);
          setEditingContract(null);
        }}
        salonId={salonId}
        onTemplateCreated={handleTemplateCreated}
        editingContract={editingContract}
      />

      <CreateDeclarationTemplateModal
        isOpen={isCreateDeclarationModalOpen}
        onClose={() => {
          setIsCreateDeclarationModalOpen(false);
          setEditingDeclaration(null);
        }}
        salonId={salonId}
        onTemplateCreated={handleDeclarationCreated}
        editingDeclaration={editingDeclaration}
      />

      <CreatePtoDecisionTemplateModal
        isOpen={isCreatePtoDecisionModalOpen}
        onClose={() => {
          setIsCreatePtoDecisionModalOpen(false);
          setEditingPtoDecisionTemplate(null);
        }}
        salonId={salonId}
        onTemplateCreated={handlePtoDecisionCreated}
        editingTemplate={editingPtoDecisionTemplate}
      />

      {isDeletePtoDecisionModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('timeOff.pto.deleteDecisionTemplate')}</h3>
              <button className="modal-close" onClick={handleCancelDeletePtoDecision}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('timeOff.pto.deleteDecisionTemplateConfirm')} "<strong>{ptoDecisionTemplateToDelete?.title}</strong>"?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="salon-button secondary"
                onClick={handleCancelDeletePtoDecision}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="salon-button danger"
                onClick={handleDeletePtoDecision}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteDeclarationModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('settings.deleteDeclarationTemplate')}</h3>
              <button className="modal-close" onClick={handleCancelDeleteDeclaration}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('settings.deleteDeclarationConfirm')} "<strong>{declarationToDelete?.title}</strong>"?</p>
              <p>{t('settings.deleteDeclarationWarning')}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="salon-button secondary"
                onClick={handleCancelDeleteDeclaration}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="salon-button danger"
                onClick={handleDeleteDeclaration}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Contract Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('settings.deleteContractTemplate')}</h3>
              <button className="modal-close" onClick={handleCancelDelete}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('settings.deleteContractConfirm')} "<strong>{contractToDelete?.title}</strong>"?</p>
              <p>{t('settings.deleteContractWarning')}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="salon-button secondary"
                onClick={handleCancelDelete}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="salon-button danger"
                onClick={handleDeleteContract}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSettings;
